// Placeholder types (da sostituire con importazioni reali o tipi da @maplibre/maplibre-gl-style-spec)
export interface MapLibrePaint extends Record<string, any> {} // E.g., { 'fill-color': string, ... }
export interface MapLibreLayout extends Record<string, any> {} // E.g., { 'visibility': 'visible' | 'none', ... }

export interface MapLibreLayer {
    id: string;
    type: 'fill' | 'line' | 'symbol' | 'circle' | 'raster' | 'background' | 'heatmap' | 'fill-extrusion' | 'hillshade';
    source?: string;
    'source-layer'?: string;
    minzoom?: number;
    maxzoom?: number;
    filter?: any[];
    layout?: MapLibreLayout;
    paint?: MapLibrePaint;
    metadata?: object;
}

export interface MapLibreSource {
    type: 'vector' | 'raster' | 'raster-dem' | 'geojson' | 'image' | 'video';
    url?: string;
    data?: any; // GeoJSON.FeatureCollection | string (URL to GeoJSON)
    tiles?: string[];
    tileSize?: number;
    minzoom?: number;
    maxzoom?: number;
    attribution?: string;
    bounds?: number[]; // [west, south, east, north]
    scheme?: 'xyz' | 'tms';
    promoteId?: string | { [sourceLayer: string]: string }; // Per vector, geojson
    volatile?: boolean; // Per vector, raster, raster-dem

    // GeoJSON specifiche
    buffer?: number;
    filter?: any; // Espressione MapLibre
    tolerance?: number;
    cluster?: boolean;
    clusterRadius?: number;
    clusterMaxZoom?: number;
    clusterMinPoints?: number;
    clusterProperties?: object; // { [propertyName: string]: Expression }
    lineMetrics?: boolean;
    generateId?: boolean;

    // Raster-DEM specifiche
    encoding?: 'terrarium' | 'mapbox' | 'custom';
    redFactor?: number;
    greenFactor?: number;
    blueFactor?: number;
    baseShift?: number;

    // Video specifiche
    urls?: string[];
    // Image/Video specifiche
    coordinates?: number[][]; // [[topLeftLng, topLeftLat], [topRightLng, topRightLat], [bottomRightLng, bottomRightLat], [bottomLeftLng, bottomLeftLat]]
}

export interface MapLibreStyle {
    version: 8;
    name?: string;
    metadata?: object;
    center?: [number, number];
    zoom?: number;
    bearing?: number;
    pitch?: number;
    sources: { [sourceId: string]: MapLibreSource };
    sprite?: string;
    glyphs?: string;
    layers: MapLibreLayer[];
}

import { VALID_LAYOUT_PROPERTIES_BY_TYPE, VALID_PAINT_PROPERTIES_BY_TYPE } from './maplibreLayerProperties';

// Assunzioni sui tipi dello store (da verificare/adattare con i tipi reali dell'applicazione)
export interface AppLayerSourceDefinition {
    type: 'geojson' | 'vector' | 'raster' | 'raster-dem' | 'image' | 'video' | string; // Tipi interni dell'applicazione, allineati con MapLibreSource
    url?: string;
    data?: any; // Potrebbe essere un oggetto GeoJSON
    tiles?: string[]; // Per vector/raster tiles
    tileSize?: number;
    minzoom?: number;
    maxzoom?: number;
    attribution?: string;
    bounds?: number[];
    scheme?: 'xyz' | 'tms';
    promoteId?: string | { [sourceLayer: string]: string };
    volatile?: boolean;

    // GeoJSON specifiche
    buffer?: number;
    filter?: any;
    tolerance?: number;
    cluster?: boolean;
    clusterMaxZoom?: number;
    clusterRadius?: number;
    clusterMinPoints?: number;
    clusterProperties?: object;
    lineMetrics?: boolean;
    generateId?: boolean;

    // Raster-DEM specifiche
    encoding?: 'terrarium' | 'mapbox' | 'custom';
    redFactor?: number;
    greenFactor?: number;
    blueFactor?: number;
    baseShift?: number;

    // Per video
    urls?: string[];
    // Per image/video
    coordinates?: number[][];

    // Proprietà specifiche dell'app che NON devono finire nella sorgente MapLibre
    name?: string;
    crs?: any;
}

export interface AppLayerDefinition {
    id: string;
    name: string;
    type: string; // Tipo di layer interno, es. 'fill-layer', 'line-layer'
    sourceId: string; // ID per recuperare la definizione della sorgente
    sourceLayer?: string; // Per sorgenti vettoriali tile
    minZoom?: number;
    maxZoom?: number;
    filter?: any[];
    isVisible?: boolean; // Per filtrare i layer attivi
}

export interface AppLayerStyleDefinition {
    paint?: MapLibrePaint;
    layout?: MapLibreLayout;
    minzoom?: number; // Aggiunto per coerenza con l'uso
    maxzoom?: number; // Aggiunto per coerenza con l'uso
    filter?: any[];   // Aggiunto per coerenza con l'uso
}

export interface AppMapViewStateDefinition {
    center: [number, number]; // [longitude, latitude]
    zoom: number;
    pitch: number;
    bearing: number;
}

/**
 * Mappa i tipi di layer dell'applicazione ai tipi di layer MapLibre.
 * @param appType Il tipo di layer dell'applicazione.
 * @returns Il tipo di layer MapLibre corrispondente.
 */
const mapApplicationLayerTypeToMapLibre = (appType: string): MapLibreLayer['type'] => {
    const typeMapping: { [key: string]: MapLibreLayer['type'] } = {
        'fill': 'fill',
        'line': 'line',
        'symbol': 'symbol',
        'circle': 'circle',
        'raster': 'raster',
        'background': 'background',
        'heatmap': 'heatmap',
        'fill-extrusion': 'fill-extrusion',
        'hillshade': 'hillshade',
    };
    const lowerAppType = appType.toLowerCase().replace('-layer', ''); // Rimuove '-layer' se presente
    if (typeMapping[lowerAppType as keyof typeof typeMapping]) {
        return typeMapping[lowerAppType as keyof typeof typeMapping];
    }
    console.warn(`Tipo di layer applicazione non mappato: ${appType}, default a 'fill'.`);
    return 'fill';
};

/**
 * Opzioni per la generazione dello stile MapLibre.
 */
export interface GenerateMapLibreStyleOptions {
    glyphsUrl: string;
    spriteUrl?: string; // Sprite è opzionale
    styleName: string;
    mapView: {
        center: [number, number];
        zoom: number;
        bearing: number;
        pitch: number;
    };
    basemap?: { // Basemap è opzionale
        tileUrl: string;
        sourceId: string;
        // Potremmo aggiungere qui 'type' se vogliamo essere espliciti (raster, vector)
        // e minzoom/maxzoom/attribution specifici per la basemap
    };
    selectedLayerIds: Set<string> | string[]; // Può essere un Set o un Array
}

/**
 * Genera un oggetto di stile MapLibre basato sullo stato corrente dell'applicazione e le opzioni fornite.
 * @param applicationLayers Array delle definizioni dei layer dell'applicazione.
 * @param applicationSources Oggetto che mappa ID sorgente a definizioni di sorgente dell'applicazione.
 * @param layerStyles Oggetto che mappa ID layer a stili (paint e layout) dell'applicazione.
 * @param options Oggetto contenente le opzioni di personalizzazione per l'esportazione.
 * @returns Un oggetto di stile compatibile con MapLibre.
 */
export const generateMapLibreStyle = (
    applicationLayers: AppLayerDefinition[],
    applicationSources: { [sourceId: string]: AppLayerSourceDefinition },
    layerStyles: { [layerId: string]: AppLayerStyleDefinition },
    options: GenerateMapLibreStyleOptions
): MapLibreStyle => {
    const mapLibreSources: { [sourceId: string]: MapLibreSource } = {};
    const mapLibreLayers: MapLibreLayer[] = []; // Inizializzato come array vuoto

    const processedSourceDefinitions = new Map<string, string>();

    // 1. Gestione Basemap (se fornita)
    if (options.basemap && options.basemap.tileUrl && options.basemap.sourceId) {
        const { tileUrl, sourceId } = options.basemap;
        // Semplificazione: assumiamo che sia un raster tile source se non diversamente specificato.
        // Una logica più avanzata potrebbe ispezionare tileUrl per capire se è un TileJSON.
        const basemapSource: MapLibreSource = {
            type: 'raster', // Assumiamo raster, potrebbe essere vector se tileUrl punta a pbf e abbiamo source-layer
            tiles: [tileUrl], // MapLibre si aspetta un array di URL template
            tileSize: 256, // Valore comune, potrebbe essere configurabile
        };
        mapLibreSources[sourceId] = basemapSource;

        const basemapLayer: MapLibreLayer = {
            id: `${sourceId}-layer`, // ID univoco per il layer della basemap
            type: 'raster', // Coerente con il tipo di sorgente
            source: sourceId,
        };
        mapLibreLayers.push(basemapLayer);
    }

    // 2. Filtra e processa i layer dell'applicazione
    const selectedLayersArray = Array.isArray(options.selectedLayerIds)
        ? options.selectedLayerIds
        : Array.from(options.selectedLayerIds);

    const layersToProcess = applicationLayers.filter(appLayer =>
        selectedLayersArray.includes(appLayer.id) && appLayer.isVisible !== false
    );

    for (const appLayer of layersToProcess) {
        if (!applicationSources.hasOwnProperty(appLayer.sourceId)) {
        }
        const sourceKeyToLookup = appLayer.sourceId;
        let sourceDefinition = applicationSources[sourceKeyToLookup];

        // Tentativo di fallback: se appLayer.sourceId è suffissato (es. "id_source_dialog")
        // e la chiave in applicationSources potesse essere non suffissata (es. "id").
        // Questo è un tentativo basato sull'ipotesi di una possibile natura della discrepanza.
        if (!sourceDefinition) {
            const commonSuffix = "_source_dialog"; // Assumiamo questo come suffisso comune e specifico
            if (appLayer.sourceId.endsWith(commonSuffix)) {
                const baseId = appLayer.sourceId.substring(0, appLayer.sourceId.length - commonSuffix.length);
                sourceDefinition = applicationSources[baseId];
                // Non aggiorniamo sourceKeyToLookup qui, perché il sourceId originale del layer è quello che
                // dovrebbe essere usato per altri scopi se non per questo lookup specifico.
                // Il log di errore userà comunque l'appLayer.sourceId originale.
            }
        }

        if (!sourceDefinition) {
            continue;
        }

        const currentLayerSpecificStyles = layerStyles[appLayer.id] as AppLayerStyleDefinition | undefined;
        const sourceMinZoomFromUserConfig = currentLayerSpecificStyles?.minzoom;
        const sourceMaxZoomFromUserConfig = currentLayerSpecificStyles?.maxzoom;
        const sourceFilterFromUserConfig = currentLayerSpecificStyles?.filter;

        let mapLibreSourceId: string;
        const appSourceType = sourceDefinition.type as MapLibreSource['type'];
        
        const isGeoJsonWithInlineDataObjectAndNoUrl = appSourceType === 'geojson' &&
                                                      typeof sourceDefinition.data === 'object' &&
                                                      sourceDefinition.data !== null &&
                                                      !Array.isArray(sourceDefinition.data) &&
                                                      !sourceDefinition.url;

        if (isGeoJsonWithInlineDataObjectAndNoUrl) {
            mapLibreSourceId = `${appLayer.id}-geojson-source`;
            if (!mapLibreSources[mapLibreSourceId]) {
                const newSource: Partial<MapLibreSource> = {
                    type: 'geojson',
                    data: `./data/${appLayer.id}.geojson`
                };
                // Applica minzoom, maxzoom, filter alla SORGENTE
                if (sourceMinZoomFromUserConfig !== undefined) {
                    newSource.minzoom = Number(sourceMinZoomFromUserConfig);
                } else if (sourceDefinition.minzoom !== undefined) { // Fallback
                    newSource.minzoom = Number(sourceDefinition.minzoom);
                }

                if (sourceMaxZoomFromUserConfig !== undefined) {
                    newSource.maxzoom = Number(sourceMaxZoomFromUserConfig);
                } else if (sourceDefinition.maxzoom !== undefined) { // Fallback
                    newSource.maxzoom = Number(sourceDefinition.maxzoom);
                }

                if (sourceFilterFromUserConfig && Array.isArray(sourceFilterFromUserConfig) && sourceFilterFromUserConfig.length > 0) {
                    newSource.filter = sourceFilterFromUserConfig;
                } else if (sourceDefinition.filter && Array.isArray(sourceDefinition.filter) && sourceDefinition.filter.length > 0) { // Fallback opzionale per filter
                    newSource.filter = sourceDefinition.filter;
                }

                // Proprietà GeoJSON comuni (eccetto minzoom, maxzoom, filter gestiti sopra)
                if (sourceDefinition.attribution !== undefined) newSource.attribution = sourceDefinition.attribution;
                if (sourceDefinition.buffer !== undefined) newSource.buffer = sourceDefinition.buffer;
                if (sourceDefinition.tolerance !== undefined) newSource.tolerance = sourceDefinition.tolerance;
                if (sourceDefinition.cluster !== undefined) newSource.cluster = sourceDefinition.cluster;
                if (sourceDefinition.clusterRadius !== undefined) newSource.clusterRadius = sourceDefinition.clusterRadius;
                if (sourceDefinition.clusterMaxZoom !== undefined) newSource.clusterMaxZoom = sourceDefinition.clusterMaxZoom;
                if (sourceDefinition.clusterMinPoints !== undefined) newSource.clusterMinPoints = sourceDefinition.clusterMinPoints;
                if (sourceDefinition.clusterProperties !== undefined) newSource.clusterProperties = sourceDefinition.clusterProperties;
                if (sourceDefinition.lineMetrics !== undefined) newSource.lineMetrics = sourceDefinition.lineMetrics;
                if (sourceDefinition.generateId !== undefined) newSource.generateId = sourceDefinition.generateId;
                if (sourceDefinition.promoteId !== undefined) newSource.promoteId = sourceDefinition.promoteId;
                mapLibreSources[mapLibreSourceId] = newSource as MapLibreSource;
            }
        } else {
            // Estendi sourceDefKeyObj per includere tutte le proprietà rilevanti per la deduplicazione
            const sourceDefKeyObj: Partial<AppLayerSourceDefinition> = {
                type: appSourceType,
                url: sourceDefinition.url,
                tiles: sourceDefinition.tiles,
                tileSize: sourceDefinition.tileSize,
                minzoom: sourceDefinition.minzoom,
                maxzoom: sourceDefinition.maxzoom,
                attribution: sourceDefinition.attribution,
                scheme: sourceDefinition.scheme,
                bounds: sourceDefinition.bounds,
                volatile: sourceDefinition.volatile,
                // GeoJSON
                buffer: sourceDefinition.buffer,
                filter: sourceDefinition.filter, // Potrebbe essere complesso per una chiave JSON, ma proviamo
                tolerance: sourceDefinition.tolerance,
                cluster: sourceDefinition.cluster,
                clusterRadius: sourceDefinition.clusterRadius,
                clusterMaxZoom: sourceDefinition.clusterMaxZoom,
                clusterMinPoints: sourceDefinition.clusterMinPoints,
                clusterProperties: sourceDefinition.clusterProperties, // Anche questo
                lineMetrics: sourceDefinition.lineMetrics,
                generateId: sourceDefinition.generateId,
                promoteId: sourceDefinition.promoteId, // Già presente per vector, ora per geojson
                // Raster-DEM
                encoding: sourceDefinition.encoding,
                redFactor: sourceDefinition.redFactor,
                greenFactor: sourceDefinition.greenFactor,
                blueFactor: sourceDefinition.blueFactor,
                baseShift: sourceDefinition.baseShift,
                // Video/Image
                coordinates: sourceDefinition.coordinates, // Già presente
                urls: sourceDefinition.urls, // Già presente per video
            };
            // Rimuovi le chiavi undefined per una sourceDefKey più pulita e consistente
            Object.keys(sourceDefKeyObj).forEach(key => {
                const k = key as keyof AppLayerSourceDefinition;
                if (sourceDefKeyObj[k] === undefined) {
                    delete sourceDefKeyObj[k];
                }
            });


            const sourceDefKey = JSON.stringify(sourceDefKeyObj);
            
            if (processedSourceDefinitions.has(sourceDefKey)) {
                mapLibreSourceId = processedSourceDefinitions.get(sourceDefKey)!;
            } else {
                mapLibreSourceId = `source-${processedSourceDefinitions.size}`;
                const newMapLibreSourceToAdd: Partial<MapLibreSource> = { type: appSourceType };

                // Copia tutte le proprietà rilevanti da sourceDefinition a newMapLibreSourceToAdd
                // Proprietà comuni a più tipi
                if (sourceDefinition.url) newMapLibreSourceToAdd.url = sourceDefinition.url;
                if (sourceDefinition.tiles) newMapLibreSourceToAdd.tiles = sourceDefinition.tiles;
                if (sourceDefinition.tileSize !== undefined) newMapLibreSourceToAdd.tileSize = sourceDefinition.tileSize;
                // minzoom, maxzoom, filter verranno gestiti sotto con priorità da layerStyles
                if (sourceDefinition.attribution !== undefined) newMapLibreSourceToAdd.attribution = sourceDefinition.attribution;
                if (sourceDefinition.bounds) newMapLibreSourceToAdd.bounds = sourceDefinition.bounds;
                if (sourceDefinition.scheme) newMapLibreSourceToAdd.scheme = sourceDefinition.scheme;
                if (sourceDefinition.volatile !== undefined) newMapLibreSourceToAdd.volatile = sourceDefinition.volatile;

                // Applica minzoom, maxzoom, filter alla SORGENTE (newMapLibreSourceToAdd)
                // Dando priorità ai valori da layerStyles, con fallback a sourceDefinition
                if (sourceMinZoomFromUserConfig !== undefined) {
                    newMapLibreSourceToAdd.minzoom = Number(sourceMinZoomFromUserConfig);
                } else if (sourceDefinition.minzoom !== undefined) { // Fallback
                    newMapLibreSourceToAdd.minzoom = Number(sourceDefinition.minzoom);
                }

                if (sourceMaxZoomFromUserConfig !== undefined) {
                    newMapLibreSourceToAdd.maxzoom = Number(sourceMaxZoomFromUserConfig);
                } else if (sourceDefinition.maxzoom !== undefined) { // Fallback
                    newMapLibreSourceToAdd.maxzoom = Number(sourceDefinition.maxzoom);
                }

                if (sourceFilterFromUserConfig && Array.isArray(sourceFilterFromUserConfig) && sourceFilterFromUserConfig.length > 0) {
                    newMapLibreSourceToAdd.filter = sourceFilterFromUserConfig;
                } else if (sourceDefinition.filter && Array.isArray(sourceDefinition.filter) && sourceDefinition.filter.length > 0) { // Fallback opzionale per filter
                    newMapLibreSourceToAdd.filter = sourceDefinition.filter;
                }

                if (appSourceType === 'geojson') {
                    if (sourceDefinition.url) { // Priorità a sourceDefinition.url se esiste
                        newMapLibreSourceToAdd.data = sourceDefinition.url;
                    } else if (typeof sourceDefinition.data === 'string' &&
                               (sourceDefinition.data.startsWith('http://') || sourceDefinition.data.startsWith('https://'))) {
                        // Altrimenti, se sourceDefinition.data è un URL, usa quello
                        newMapLibreSourceToAdd.data = sourceDefinition.data;
                    }
                    // else {
                    //   // Caso in cui non c'è né sourceDefinition.url né sourceDefinition.data come URL.
                    //   // Potrebbe essere un oggetto GeoJSON inline (ma questo caso dovrebbe essere gestito da isGeoJsonWithInlineDataObjectAndNoUrl)
                    //   // o un errore. Per ora, se non è un URL, non impostiamo 'data' qui,
                    //   // lasciando che MapLibre gestisca l'eventuale assenza o gestisca l'oggetto se passato.
                    //   // Se sourceDefinition.data è un oggetto GeoJSON, potrebbe essere copiato direttamente se necessario,
                    //   // ma la logica di isGeoJsonWithInlineDataObjectAndNoUrl dovrebbe averlo già gestito.
                    //   // Questa parte potrebbe richiedere ulteriore affinamento se ci sono casi non coperti.
                    // }

                    // Copia le altre proprietà specifiche di GeoJSON
                    if (sourceDefinition.buffer !== undefined) newMapLibreSourceToAdd.buffer = sourceDefinition.buffer;
                    if (sourceDefinition.tolerance !== undefined) newMapLibreSourceToAdd.tolerance = sourceDefinition.tolerance;
                    if (sourceDefinition.cluster !== undefined) newMapLibreSourceToAdd.cluster = sourceDefinition.cluster;
                    if (sourceDefinition.clusterRadius !== undefined) newMapLibreSourceToAdd.clusterRadius = sourceDefinition.clusterRadius;
                    if (sourceDefinition.clusterMaxZoom !== undefined) newMapLibreSourceToAdd.clusterMaxZoom = sourceDefinition.clusterMaxZoom;
                    if (sourceDefinition.clusterMinPoints !== undefined) newMapLibreSourceToAdd.clusterMinPoints = sourceDefinition.clusterMinPoints;
                    if (sourceDefinition.clusterProperties !== undefined) newMapLibreSourceToAdd.clusterProperties = sourceDefinition.clusterProperties;
                    if (sourceDefinition.lineMetrics !== undefined) newMapLibreSourceToAdd.lineMetrics = sourceDefinition.lineMetrics;
                    if (sourceDefinition.generateId !== undefined) newMapLibreSourceToAdd.generateId = sourceDefinition.generateId;
                    if (sourceDefinition.promoteId) newMapLibreSourceToAdd.promoteId = sourceDefinition.promoteId;
                } else if (appSourceType === 'vector') {
                    if (sourceDefinition.promoteId) newMapLibreSourceToAdd.promoteId = sourceDefinition.promoteId;
                    // volatile, tiles, tileSize, scheme, attribution, minzoom, maxzoom, bounds già gestiti sopra
                } else if (appSourceType === 'raster') {
                    // volatile, tiles, tileSize, scheme, attribution, minzoom, maxzoom, bounds già gestiti sopra
                } else if (appSourceType === 'raster-dem') {
                    if (sourceDefinition.encoding) newMapLibreSourceToAdd.encoding = sourceDefinition.encoding;
                    if (sourceDefinition.redFactor !== undefined) newMapLibreSourceToAdd.redFactor = sourceDefinition.redFactor;
                    if (sourceDefinition.greenFactor !== undefined) newMapLibreSourceToAdd.greenFactor = sourceDefinition.greenFactor;
                    if (sourceDefinition.blueFactor !== undefined) newMapLibreSourceToAdd.blueFactor = sourceDefinition.blueFactor;
                    if (sourceDefinition.baseShift !== undefined) newMapLibreSourceToAdd.baseShift = sourceDefinition.baseShift;
                    // volatile, tiles, tileSize, attribution, minzoom, maxzoom, bounds già gestiti sopra
                } else if (appSourceType === 'image') {
                    if (sourceDefinition.coordinates) newMapLibreSourceToAdd.coordinates = sourceDefinition.coordinates;
                    // url già gestito sopra
                } else if (appSourceType === 'video') {
                    if (sourceDefinition.coordinates) newMapLibreSourceToAdd.coordinates = sourceDefinition.coordinates;
                    if (sourceDefinition.urls) {
                        newMapLibreSourceToAdd.urls = sourceDefinition.urls;
                        delete newMapLibreSourceToAdd.url; // urls è preferito per video
                    } else if (newMapLibreSourceToAdd.url) { // Fallback se solo url è fornito
                        newMapLibreSourceToAdd.urls = [newMapLibreSourceToAdd.url];
                        delete newMapLibreSourceToAdd.url;
                    }
                } else if (appSourceType) {
                } else {
                }
                
                if (!newMapLibreSourceToAdd.type && appSourceType) {
                    newMapLibreSourceToAdd.type = appSourceType;
                }

                if (newMapLibreSourceToAdd.type) {
                    mapLibreSources[mapLibreSourceId] = newMapLibreSourceToAdd as MapLibreSource;
                    processedSourceDefinitions.set(sourceDefKey, mapLibreSourceId);
                } else {
                }
            }
        }

        const style = layerStyles[appLayer.id];
        const rawPaintProperties = style?.paint || {};
        const rawLayoutProperties = style?.layout || {};
        
        const layerType = mapApplicationLayerTypeToMapLibre(appLayer.type);
        
        const validPaintProps = VALID_PAINT_PROPERTIES_BY_TYPE[layerType] || [];
        const validLayoutProps = VALID_LAYOUT_PROPERTIES_BY_TYPE[layerType] || [];

        const filteredPaintProperties: MapLibrePaint = {};
        for (const key in rawPaintProperties) {
            if (validPaintProps.includes(key)) {
                filteredPaintProperties[key] = rawPaintProperties[key];
            }
        }

        const filteredLayoutProperties: MapLibreLayout = {};
        for (const key in rawLayoutProperties) {
            if (validLayoutProps.includes(key)) {
                filteredLayoutProperties[key] = rawLayoutProperties[key];
            }
        }

        const mapLibreLayerTyped: MapLibreLayer = {
            id: appLayer.id,
            type: layerType,
            source: mapLibreSourceId,
        };

        // Aggiungi paint/layout solo se hanno proprietà dopo il filtraggio
        if (Object.keys(filteredPaintProperties).length > 0) {
            mapLibreLayerTyped.paint = filteredPaintProperties;
        }
        if (Object.keys(filteredLayoutProperties).length > 0) {
            mapLibreLayerTyped.layout = filteredLayoutProperties;
        }

        if (appLayer.sourceLayer) {
            mapLibreLayerTyped['source-layer'] = appLayer.sourceLayer;
        }

        // La visibilità è già gestita dal filtro `layersToProcess`
        // Se un layer è `isVisible === false`, non viene incluso.
        // Se `isVisible` è true o undefined, viene incluso e la sua visibilità
        // nello stile MapLibre dipenderà da `layout.visibility` se definito,
        // altrimenti sarà visibile di default.

        mapLibreLayers.push(mapLibreLayerTyped);
    }

    const finalStyle: MapLibreStyle = {
        version: 8,
        name: options.styleName,
        center: [Number(options.mapView.center[0].toFixed(3)), Number(options.mapView.center[1].toFixed(3))],
        zoom: Number(options.mapView.zoom.toFixed(3)),
        bearing: Number(options.mapView.bearing.toFixed(3)),
        pitch: Number(options.mapView.pitch.toFixed(3)),
        sources: mapLibreSources,
        layers: mapLibreLayers, // mapLibreLayers ora contiene prima la basemap (se presente)
        glyphs: options.glyphsUrl,
    };

    if (options.spriteUrl) {
        finalStyle.sprite = options.spriteUrl;
    }

    return finalStyle;
};